import { FocusEvent, KeyboardEvent, useEffect, useRef, useState } from 'react'

export type RovingTabIndexOrientation = 'horizontal' | 'vertical'

export type UseRovingTabIndexProps = {
  /** The full ordered list of navigable item values. */
  values: string[]
  /** The externally-committed/active value, if any. Whenever focus isn't inside the group, the tabbable
   *  item tracks this directly (rather than a stale roving position left over from the last time the
   *  group had focus). */
  activeValue?: string
  /** Which arrow keys move the roving position: ArrowLeft/ArrowRight for 'horizontal' (default),
   *  ArrowUp/ArrowDown for 'vertical'. Home/End always jump to the first/last item either way. */
  orientation?: RovingTabIndexOrientation
}

/**
 * Single-axis (1D) roving-tabindex keyboard navigation for a composite widget (toolbar, tablist, button
 * group, ...) where arrow keys should move focus WITHOUT necessarily committing a selection -- that part
 * is left entirely to each item's own native activation (a real <button>'s onClick already fires on
 * Enter/Space/click), so this hook only ever needs to track which item currently has focus/tabindex=0.
 *
 * Use this (rather than `useKeyboardAccessibleGrid` in
 * `~/design_system/foundations/accessibility/KeyboardAccessibleGrid`) when your items form a single
 * horizontal or vertical line -- a row of filter tabs, a toolbar, a menu -- and selecting one shouldn't
 * happen just by arrowing past it. Reach for `useKeyboardAccessibleGrid` instead when items are laid out
 * in an actual 2D grid (rows AND columns, e.g. a master-detail list with a checkbox column): it wraps
 * Left/Right across row boundaries, clamps Up/Down, and lets each cell's own native element (button,
 * checkbox, link) handle activation immediately on focus -- it has no "focus moves, selection doesn't
 * commit yet" concept, so it isn't a fit for widgets like FilterTabs where that distinction matters.
 *
 * Each navigable item must, within the element `containerRef` is attached to:
 * - set `tabIndex={tabbableValue === itemValue ? 0 : -1}`
 * - call `onItemFocus(itemValue)` from its own onFocus handler
 *
 * The container must spread `onKeyDown={handleKeyDown}`, `onFocus={handleContainerFocus}`, and
 * `onBlur={handleContainerBlur}`.
 */
export function useRovingTabIndex<TElement extends HTMLElement = HTMLDivElement>({
  values,
  activeValue,
  orientation = 'horizontal'
}: UseRovingTabIndexProps) {
  const containerRef = useRef<TElement>(null)
  const [rovingValue, setRovingValue] = useState(activeValue)
  const [isFocusedWithin, setIsFocusedWithin] = useState(false)

  // Only trust the roving position while focus is actually inside the group; otherwise always reflect
  // the current active value directly, so there's nothing to "resync" via an effect when it changes.
  const tabbableValue = (isFocusedWithin ? rovingValue : undefined) ?? activeValue ?? values[0]

  // Move real DOM focus to match the roving tabindex after arrow-key navigation.
  useEffect(() => {
    if (!isFocusedWithin) return
    const rovingItem = containerRef.current?.querySelector<HTMLElement>('[tabindex="0"]')
    if (rovingItem && rovingItem !== document.activeElement) rovingItem.focus()
  }, [rovingValue, isFocusedWithin])

  const nextKey = orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown'
  const previousKey = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp'

  const handleKeyDown = (event: KeyboardEvent) => {
    const currentIndex = values.indexOf(rovingValue ?? '')
    if (currentIndex === -1) return

    switch (event.key) {
      case nextKey:
        event.preventDefault()
        setRovingValue(values[(currentIndex + 1) % values.length])
        break
      case previousKey:
        event.preventDefault()
        setRovingValue(values[(currentIndex - 1 + values.length) % values.length])
        break
      case 'Home':
        event.preventDefault()
        setRovingValue(values[0])
        break
      case 'End':
        event.preventDefault()
        setRovingValue(values[values.length - 1])
        break
      default:
        break
    }
  }

  const handleContainerFocus = () => setIsFocusedWithin(true)
  const handleContainerBlur = (event: FocusEvent) => {
    if (!containerRef.current?.contains(event.relatedTarget as Node | null)) setIsFocusedWithin(false)
  }

  return {
    containerRef,
    tabbableValue,
    handleKeyDown,
    handleContainerFocus,
    handleContainerBlur,
    onItemFocus: setRovingValue
  }
}
